<template>
  <div>
    <app-cells>
      <app-button @click.native="onShowModal">Import</app-button>
    </app-cells>
    <app-cells>
      <div class="date-picker-wrapper">
        <VueCtkDateTimePicker
          id="date_picker_balance"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          @is-hidden="onIsHiddenPicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Select dates"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
        />
        <div v-if="date_picker_error" class="date-picker-wrapper__error">Choose an end date</div>
      </div>
      <app-input
        @input.native="onSearch"
        v-model="filter_search"
        class="input--300"
        type="search"
        placeholder="Search"
        name="search_invoice"
      />
    </app-cells>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells><h2 class="title">Allocated</h2></app-cells>
      <v-client-table
        :data="table_allocated.items"
        :columns="table_allocated.columns"
        :options="table_allocated.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader_allocated }"
      >
        <template #h__account><div class="table-default__left">Account</div></template>
        <template #h__description><div class="table-default__left">Description</div></template>
        <template #h__reference><div class="table-default__left">Reference</div></template>
        <template #h__rule><div class="table-default__left">Rule</div></template>
        <template #account="{ row }"><div class="table-default__left table-default__nowrap">{{ row.account.name }}</div></template>
        <template #date="{ row }"><span class="table-default__nowrap">{{ dateTimeFormat(row.timestamp) }}</span></template>
        <template #reference="{ row }"><div class="table-default__left">{{ row.reference }}</div></template>
        <template #description="{ row }"><div class="table-default__left">{{ row.description }}</div></template>
        <template #amount="{ row }">
          <span class="table-default__nowrap" :class="{'table-default__text-green': row.type === 1, 'table-default__text-red': row.type === 2}">
          {{ amountFormat(row.amount) }} {{ row.account.currency.code }}
          </span>
        </template>
        <template #rule="{ row }"><div class="table-default__left">{{ row.rule?.description || row.rule?.pattern || '-' }}</div></template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationAllocated"
            v-if="pagination_allocated.pages > 1"
            v-model="pagination_allocated.page"
            :page-count="pagination_allocated.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_allocated.count" :count="table_allocated.items.length" :page="pagination_allocated.page" :limit="40" />
      </app-cells>
    </template>
    <modal
      name="import_modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      :reset="true"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button @click="$modal.hide('import_modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Import Stripe Statement</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Period" required>
              <app-date-picker
                v-model="period"
                range
                :clearable="false"
                placeholder="Period"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button ref="submit" type="submit">Import</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getFinanceCashflowReconciliation,
  postFinanceReconciliationImportStripe
} from '@/http'
import moment from 'moment/moment'
import debounce from 'lodash/debounce'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { format, parseISO, subDays } from 'date-fns'

export default {
  name: 'FinanceCashflowReconciliationStripe',
  components: { VueCtkDateTimePicker },
  data() {
    return {
      page_loaded: false,
      period: [],
      filter_dates: null,
      date_picker_error: false,
      filter_search: '',
      custom_shortcuts: [
        {
          key: 'next30Days',
          label: 'Next 30 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(30, 'days')
            }
          }
        },
        {
          key: 'next60Days',
          label: 'Next 60 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(60, 'days')
            }
          }
        },
        {
          key: 'next90Days',
          label: 'Next 90 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(90, 'days')
            }
          }
        },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'thisYear', label: 'This year', value: 'year' },
      ],
      table_allocated: {
        items: [],
        columns: [
          'date',
          'account',
          'amount',
          'reference',
          'description',
          'rule',
        ],
        options: {
          headings: {
            'date': 'Date',
            'account': 'Account',
            'amount': 'Amount',
            'reference': 'Reference',
            'description': 'Description',
            'rule': 'Rule',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_allocated: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_allocated: false,
    }
  },
  mounted() {
    this.fetchItemsAllocated()
      .then(() => {
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItemsAllocated() {
      const response = await getFinanceCashflowReconciliation({
        page: this.pagination_allocated.page,
        page_size: 40,
        is_allocated: true,
        source: 'stripe',
        timestamp__gte: this.filter_dates?.start,
        timestamp__lte: this.filter_dates?.end,
        search: this.filter_search,
      })
      this.table_allocated.items = response.data.results
      this.pagination_allocated.pages = response.data.pages
      this.pagination_allocated.count = response.data.count
      this.table_preloader_allocated = false
    },
    onClickPaginationAllocated(page) {
      this.pagination_allocated.page = page
      this.fetchItemsAllocated()
    },
    onCheckForm() {
      this.$refs.submit.preload = true
      postFinanceReconciliationImportStripe({ from_date: this.period[0], to_date: this.period[1] })
        .then(() => {
          this.fetchItemsAllocated()
          this.$notify({
            type: 'success',
            title: 'Imported',
          })
          this.$modal.hide('import_modal')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onShowModal() {
      this.period = [format(subDays(new Date(), 2), 'yyyy-MM-dd'), format(new Date(), 'yyyy-MM-dd')]
      this.$modal.show('import_modal')
    },
    onIsHiddenPicker() {
      if (this.filter_dates?.start && this.filter_dates?.end) {
        this.date_picker_error = false
        this.fetchItemsAllocated()
      } else this.date_picker_error = true
    },
    onSearch() {
      this.searchItem(this)
    },
    searchItem: debounce(vm => {
      vm.pagination_allocated.page = 1
      vm.fetchItemsAllocated()
    }, 350),
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>
